'use client';

import React, { useState, useEffect, useRef } from 'react';
import { Mic, Send, Bot, User, Settings, Square, Plus, Trash2, MessageSquare, Menu, X, LogOut, Eye, EyeOff } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';

interface Message {
  id: string;
  text: string;
  sender: 'user' | 'hermes';
  timestamp: string;
  dateKey: string;
  fullDate: string;
}

interface Conversation {
  id: string;
  title: string;
  messages: Message[];
  lastUpdated: number;
  isEnded?: boolean;
}

export default function HermesApp() {
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [input, setInput] = useState('');
  const [isListening, setIsListening] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [mounted, setMounted] = useState(false);
  
  // Auth state
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [pinInput, setPinInput] = useState('');
  const [showPin, setShowPin] = useState(false);
  const [authError, setAuthError] = useState(false);

  const scrollRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Load from localStorage
  useEffect(() => {
    setMounted(true);
    
    const savedPin = localStorage.getItem('hermes_access_pin');
    if (savedPin) {
      setIsAuthenticated(true);
    }
    
    const saved = localStorage.getItem('hermes_conversations');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        setConversations(parsed);
        if (parsed.length > 0) {
          setActiveId(parsed[0].id);
        }
      } catch (e) {
        console.error('Error loading conversations', e);
      }
    } else {
      createNewChat();
    }
  }, []);

  // Save to localStorage
  useEffect(() => {
    if (mounted && conversations.length > 0) {
      localStorage.setItem('hermes_conversations', JSON.stringify(conversations));
    }
  }, [conversations, mounted]);

  // Auto-resize del textarea
  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = '44px';
      if (input) {
        textarea.style.height = `${Math.min(textarea.scrollHeight, 250)}px`;
      }
    }
  }, [input]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [activeId, conversations]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsProcessing(true);
    try {
      const isLocal = typeof window !== 'undefined' && window.location.port === '4001';
      const apiUrl = isLocal 
        ? `${window.location.protocol}//${window.location.hostname}:4000`
        : (typeof window !== 'undefined' ? `${window.location.protocol}//${window.location.host}` : '');
        
      const res = await fetch(`${apiUrl}/chat`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${pinInput}` 
        },
        body: JSON.stringify({ message: '/ping' })
      });
      if (res.ok) {
        localStorage.setItem('hermes_access_pin', pinInput);
        setIsAuthenticated(true);
        setAuthError(false);
      } else {
        const errorData = await res.text();
        console.error('Error de autenticación:', res.status, errorData);
        setAuthError(true);
      }
    } catch (error) {
      console.error('Fallo en fetch de login:', error);
      setAuthError(true);
    }
    setIsProcessing(false);
  };

  const activeChat = conversations.find(c => c.id === activeId) || null;

  const createNewChat = () => {
    const newChat: Conversation = {
      id: Date.now().toString(),
      title: 'Nueva Conversación',
      messages: [
        { 
          id: '1', 
          text: 'Hola, soy Hermes. ¿En qué puedo ayudarte hoy?', 
          sender: 'hermes', 
          timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
          dateKey: new Date().toISOString().split('T')[0],
          fullDate: new Date().toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long' })
        }
      ],
      lastUpdated: Date.now()
    };
    setConversations(prev => [newChat, ...prev]);
    setActiveId(newChat.id);
    setIsSidebarOpen(false);
  };

  const deleteChat = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    const updated = conversations.filter(c => c.id !== id);
    setConversations(updated);
    if (activeId === id) {
      setActiveId(updated.length > 0 ? updated[0].id : null);
    }
    if (updated.length === 0) {
      createNewChat();
    }
  };

  const stopHermes = () => {
    if (window.speechSynthesis) {
      window.speechSynthesis.cancel();
      setIsSpeaking(false);
    }
  };

  const handleSend = async (text: string, isVoice: boolean = false) => {
    const cleanText = text.trim();
    if (!cleanText || !activeId || activeChat?.isEnded) return;

    // Handle /exit command
    if (cleanText.toLowerCase() === '/exit') {
      const exitMsg: Message = {
        id: Date.now().toString(),
        text: cleanText,
        sender: 'user',
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        dateKey: new Date().toISOString().split('T')[0],
        fullDate: new Date().toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long' })
      };
      
      const responseMsg: Message = {
        id: (Date.now() + 1).toString(),
        text: 'Sesión finalizada. Gracias por usar Hermes. ¡Hasta pronto!',
        sender: 'hermes',
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        dateKey: new Date().toISOString().split('T')[0],
        fullDate: new Date().toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long' })
      };

      setConversations(prev => prev.map(c => 
        c.id === activeId 
          ? { ...c, messages: [...c.messages, exitMsg, responseMsg], isEnded: true, lastUpdated: Date.now() }
          : c
      ));
      setInput('');
      return;
    }

    const userMsg: Message = {
      id: Date.now().toString(),
      text: cleanText,
      sender: 'user',
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      dateKey: new Date().toISOString().split('T')[0],
      fullDate: new Date().toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long' })
    };

    // Update UI immediately
    setConversations(prev => prev.map(c => {
      if (c.id === activeId) {
        const newTitle = c.messages.length === 1 ? (cleanText.length > 25 ? cleanText.substring(0, 25) + '...' : cleanText) : c.title;
        return { ...c, messages: [...c.messages, userMsg], title: newTitle, lastUpdated: Date.now() };
      }
      return c;
    }));

    setInput('');
    setIsProcessing(true);

    try {
       const isLocal = typeof window !== 'undefined' && window.location.port === '4001';
       const apiUrl = isLocal 
         ? `${window.location.protocol}//${window.location.hostname}:4000`
         : (typeof window !== 'undefined' ? `${window.location.protocol}//${window.location.host}` : '');
         
       const pin = localStorage.getItem('hermes_access_pin') || '';
       const res = await fetch(`${apiUrl}/chat`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${pin}`
        },
        body: JSON.stringify({ message: cleanText })
      });
      
      if (res.status === 401) {
        setIsAuthenticated(false);
        localStorage.removeItem('hermes_access_pin');
        setIsProcessing(false);
        return;
      }
      
      const data = await res.json();

      const hermesMsg: Message = {
        id: (Date.now() + 1).toString(),
        text: data.response,
        sender: 'hermes',
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        dateKey: new Date().toISOString().split('T')[0],
        fullDate: new Date().toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long' })
      };

      setConversations(prev => prev.map(c => 
        c.id === activeId 
          ? { ...c, messages: [...c.messages, hermesMsg], lastUpdated: Date.now() }
          : c
      ));
      
      if (isVoice) {
        setIsSpeaking(true);
        const u = new SpeechSynthesisUtterance(data.response);
        u.lang = 'es-ES';
        u.onend = () => setIsSpeaking(false);
        u.onerror = () => setIsSpeaking(false);
        window.speechSynthesis.speak(u);
      }
    } catch (error) {
      console.error(error);
      const errMsg: Message = { id: `err-${Date.now()}`, text: 'Error conectando al puente local.', sender: 'hermes', timestamp: '', dateKey: '', fullDate: '' };
      setConversations(prev => prev.map(c => 
        c.id === activeId ? { ...c, messages: [...c.messages, errMsg] } : c
      ));
    } finally {
      setIsProcessing(false);
    }
  };

  const toggleVoice = () => {
    const SpeechRecognition = (window as any).webkitSpeechRecognition || (window as any).SpeechRecognition;
    if (!SpeechRecognition) return alert('Usá Chrome o Edge');

    const recognition = new SpeechRecognition();
    recognition.lang = 'es-ES';
    
    if (!isListening) {
      recognition.start();
      setIsListening(true);
    } else {
      recognition.stop();
      setIsListening(false);
    }

    recognition.onresult = (e: any) => {
      handleSend(e.results[0][0].transcript, true);
      setIsListening(false);
    };
    recognition.onerror = () => setIsListening(false);
    recognition.onend = () => setIsListening(false);
  };

  if (!mounted) return null;

  if (!isAuthenticated) {
    return (
      <main className="min-h-screen bg-black flex flex-col items-center justify-center p-4">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="glass p-8 rounded-3xl max-w-sm w-full border-cyan-900/30 shadow-[0_0_40px_rgba(6,182,212,0.15)] text-center relative overflow-hidden"
        >
          <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-cyan-500 to-transparent opacity-50" />
          
          <div className="w-16 h-16 bg-gradient-to-br from-cyan-950 to-black border border-cyan-900/50 rounded-full flex items-center justify-center mx-auto mb-6 shadow-[0_0_20px_rgba(6,182,212,0.2)]">
            <Bot size={32} className="text-cyan-400" />
          </div>
          
          <h1 className="text-2xl font-light text-white mb-2 tracking-wide">Hermes Access</h1>
          <p className="text-zinc-400 text-sm mb-8 font-light">Agente Personal Restringido</p>
          
          <form onSubmit={handleLogin} className="space-y-4">
            <div className="relative">
              <input
                type={showPin ? "text" : "password"}
                value={pinInput}
                onChange={(e) => setPinInput(e.target.value)}
                placeholder="Introduzca PIN"
                className="w-full bg-black/50 border border-white/10 rounded-xl px-4 py-3 text-center text-white tracking-widest focus:outline-none focus:border-cyan-500/50 focus:ring-1 focus:ring-cyan-500/50 transition-all font-mono pr-12"
                autoFocus
              />
              <button
                type="button"
                onClick={() => setShowPin(!showPin)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-cyan-400 transition-colors p-1"
              >
                {showPin ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
            
            <AnimatePresence>
              {authError && (
                <motion.p 
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="text-red-400 text-xs font-medium"
                >
                  PIN incorrecto. Acceso denegado.
                </motion.p>
              )}
            </AnimatePresence>
            
            <button
              type="submit"
              disabled={isProcessing || !pinInput}
              className="w-full bg-cyan-950 hover:bg-cyan-900 text-cyan-400 font-medium py-3 rounded-xl border border-cyan-900/50 transition-all disabled:opacity-50 disabled:cursor-not-allowed uppercase tracking-widest text-xs"
            >
              {isProcessing ? 'Verificando...' : 'Desbloquear'}
            </button>
          </form>
        </motion.div>
      </main>
    );
  }

  return (
    <div className="flex h-screen bg-[#050505] text-white font-sans overflow-hidden">
      {/* Sidebar Overlay */}
      <AnimatePresence>
        {isSidebarOpen && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setIsSidebarOpen(false)}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 lg:hidden"
          />
        )}
      </AnimatePresence>

      {/* Sidebar */}
      <aside className={`fixed lg:static inset-y-0 left-0 w-72 glass border-r border-white/5 z-50 transition-transform duration-300 lg:translate-x-0 ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="p-6 h-full flex flex-col">
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-cyan-500/20 flex items-center justify-center border border-cyan-500/50">
                <Bot className="text-cyan-400" size={18} />
              </div>
              <div className="flex flex-col">
                <span className="font-bold tracking-tight text-lg leading-tight">HERMES</span>
                <span className="text-[9px] text-zinc-500 uppercase tracking-widest font-medium">AI Agent • v1.0.0</span>
              </div>
            </div>
            <button onClick={() => setIsSidebarOpen(false)} className="lg:hidden p-2 hover:bg-white/5 rounded-full">
              <X size={20} />
            </button>
          </div>

          <button 
            onClick={createNewChat}
            className="w-full flex items-center gap-3 p-4 mb-6 rounded-xl border border-white/10 bg-white/5 hover:bg-white/10 transition-all group"
          >
            <Plus size={18} className="text-cyan-400 group-hover:scale-110 transition-transform" />
            <span className="text-sm font-medium">Nuevo Chat</span>
          </button>

          <div className="flex-1 overflow-y-auto space-y-2 pr-2 custom-scrollbar">
            {conversations.map(chat => (
              <div 
                key={chat.id}
                onClick={() => { setActiveId(chat.id); setIsSidebarOpen(false); }}
                className={`group relative flex items-center gap-3 p-3.5 rounded-xl cursor-pointer transition-all ${activeId === chat.id ? 'bg-cyan-500/10 border border-cyan-500/20 text-white' : 'hover:bg-white/5 text-zinc-400 border border-transparent'}`}
              >
                <MessageSquare size={16} className={activeId === chat.id ? 'text-cyan-400' : 'text-zinc-500'} />
                <span className="text-xs font-medium truncate flex-1">{chat.title}</span>
                <button 
                  onClick={(e) => deleteChat(chat.id, e)}
                  className="p-1 text-zinc-500 hover:text-red-400 transition-all"
                >
                  <Trash2 size={14} />
                </button>

              </div>
            ))}
          </div>

          <div className="mt-auto pt-6 border-t border-white/5">
            <div className="flex items-center gap-3 p-2">
              <div className="w-8 h-8 rounded-full bg-zinc-800 flex items-center justify-center">
                <User size={16} className="text-zinc-400" />
              </div>
              <div className="flex-1">
                <p className="text-xs font-bold truncate">Demo User</p>
                <p className="text-[10px] text-zinc-500">Premium Plan</p>
              </div>
              <Settings size={16} className="text-zinc-500 cursor-pointer hover:text-white" />
            </div>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 flex flex-col relative h-full overflow-hidden">
        <header className="p-4 lg:p-6 flex justify-between items-center glass z-10 border-b border-white/5">
          <div className="flex items-center gap-4">
            <button onClick={() => setIsSidebarOpen(true)} className="lg:hidden p-2 hover:bg-white/5 rounded-full">
              <Menu size={22} />
            </button>
            <div className="flex flex-col">
              <h2 className="text-[10px] font-bold tracking-tight text-zinc-500 uppercase leading-none mb-1">Conversación</h2>
              <h1 className="text-lg font-bold truncate max-w-[200px] lg:max-w-md leading-tight">{activeChat?.title}</h1>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
             <div className="flex items-center gap-1.5 bg-zinc-900/50 px-3 py-1.5 rounded-full border border-white/5">
              <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
              <span className="text-[10px] text-zinc-400 uppercase tracking-widest font-medium">Server Online</span>
            </div>
          </div>
        </header>

        <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 lg:p-6 space-y-1 custom-scrollbar">
          <AnimatePresence>
            {activeChat?.messages.map((msg, index) => {
              const showDate = index === 0 || msg.dateKey !== activeChat.messages[index - 1].dateKey;
              return (
                <div key={msg.id}>
                  {showDate && msg.fullDate && (
                    <div className="flex justify-center my-12">
                      <div className="flex flex-col items-center gap-2">
                          <div className="h-[1px] w-20 bg-gradient-to-r from-transparent via-yellow-400/50 to-transparent" />
                          <span className="text-[11px] font-black text-yellow-400 uppercase tracking-[0.5em] bg-yellow-400/10 px-6 py-2 rounded-full border border-yellow-400/20 shadow-[0_0_20px_rgba(250,204,21,0.1)]">
                              {msg.fullDate}
                          </span>
                          <div className="h-[1px] w-20 bg-gradient-to-r from-transparent via-yellow-400/50 to-transparent" />
                      </div>
                    </div>
                  )}
                  <div className={`flex ${msg.sender === 'user' ? 'justify-end' : 'justify-start'}`}>
                    <motion.div 
                      initial={{ opacity: 0, scale: 0.95 }} 
                      animate={{ opacity: 1, scale: 1 }} 
                      className={`px-3 py-1.5 rounded-2xl max-w-[95%] border shadow-lg ${
                        msg.sender === 'user' 
                          ? 'bg-green-950/40 border-green-500/30 text-green-50' 
                          : 'bg-zinc-900/50 border-white/10 border-l-4 border-l-cyan-500 shadow-cyan-500/5'
                      }`}
                    >
                      <div className="text-[13px] leading-relaxed markdown-content">
                        {msg.sender === 'user' ? (
                          <span className="whitespace-pre-wrap">{msg.text}</span>
                        ) : (
                          <ReactMarkdown 
                            remarkPlugins={[remarkGfm, remarkBreaks]}
                            components={{
                              h1: ({node, ...props}) => <h1 className="text-lg font-bold mb-4 text-cyan-400 border-b border-cyan-500/10 pb-1" {...props} />,
                              h2: ({node, ...props}) => <h2 className="text-base font-bold mt-5 mb-3 text-white/90" {...props} />,
                              h3: ({node, ...props}) => <h3 className="text-sm font-bold mt-4 mb-2 text-white/80" {...props} />,
                              p: ({node, ...props}) => <p className="mb-3 last:mb-0 whitespace-pre-wrap" {...props} />,
                              ul: ({node, ...props}) => <ul className="list-disc ml-5 mb-3 space-y-1" {...props} />,
                              ol: ({node, ...props}) => <ol className="list-decimal ml-5 mb-3 space-y-1" {...props} />,
                              li: ({node, ...props}) => <li className="pl-1 mb-0.5" {...props} />,
                              code: ({node, ...props}) => <code className="bg-black/40 px-1 py-0.5 rounded font-mono text-[12px] text-cyan-300 border border-white/5" {...props} />,
                              pre: ({node, ...props}) => <pre className="bg-black/60 p-3 rounded-xl overflow-x-auto border border-white/5 my-3 custom-scrollbar" {...props} />,
                              table: ({node, ...props}) => <div className="overflow-x-auto my-3"><table className="w-full border-collapse border border-white/5 rounded-lg overflow-hidden text-[12px]" {...props} /></div>,
                              th: ({node, ...props}) => <th className="bg-white/5 border border-white/5 p-2 text-left font-bold" {...props} />,
                              td: ({node, ...props}) => <td className="border border-white/5 p-2 text-zinc-400" {...props} />,
                              a: ({node, ...props}) => <a className="text-cyan-500 hover:underline" {...props} />,
                              blockquote: ({node, ...props}) => <blockquote className="border-l-2 border-zinc-800 pl-4 italic my-3 text-zinc-500" {...props} />,
                            }}
                          >
                            {msg.text}
                          </ReactMarkdown>
                        )}
                        {msg.timestamp && (
                          <span className="text-[9px] opacity-30 font-mono tracking-tighter ml-2 align-bottom whitespace-nowrap">[{msg.timestamp}]</span>
                        )}
                      </div>
                    </motion.div>
                  </div>
                </div>
              );
            })}
            
            {activeChat?.isEnded && (
              <motion.div 
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="flex justify-center my-8"
              >
                <div className="bg-red-500/10 border border-red-500/20 px-6 py-3 rounded-full flex items-center gap-3">
                  <LogOut size={16} className="text-red-400" />
                  <span className="text-xs font-bold text-red-400 uppercase tracking-widest">Sesión Finalizada</span>
                </div>
              </motion.div>
            )}

            {isProcessing && (
              <div className="flex justify-start">
                <div className="glass p-4 rounded-2xl border-cyan-900/20 flex items-center gap-3">
                  <div className="flex gap-1">
                    <span className="w-1.5 h-1.5 bg-cyan-500 rounded-full animate-bounce" />
                    <span className="w-1.5 h-1.5 bg-cyan-500 rounded-full animate-bounce [animation-delay:0.2s]" />
                    <span className="w-1.5 h-1.5 bg-cyan-500 rounded-full animate-bounce [animation-delay:0.4s]" />
                  </div>
                  <p className="text-xs text-cyan-500 font-medium uppercase tracking-wider">Procesando</p>
                </div>
              </div>
            )}
          </AnimatePresence>
        </div>

        {/* Input Area */}
        <div className="p-4 lg:p-6 bg-black z-20 border-t border-white/5">
          <div className="max-w-3xl mx-auto flex items-end gap-3">
            
            {isSpeaking ? (
              <button onClick={stopHermes} className="p-4 rounded-full bg-red-600 hover:bg-red-500 transition-colors shadow-[0_0_20px_rgba(220,38,38,0.4)] flex-shrink-0">
                <Square size={22} className="text-white fill-current" />
              </button>
            ) : (
              <button 
                onClick={toggleVoice} 
                disabled={activeChat?.isEnded}
                className={`p-4 rounded-full transition-all flex-shrink-0 ${isListening ? 'bg-cyan-600 animate-pulse scale-110' : 'bg-zinc-800 hover:bg-zinc-700 disabled:opacity-50 disabled:cursor-not-allowed'}`}
              >
                <Mic size={18} className="text-white" />
              </button>
            )}

            <div className="relative flex-1 group bg-zinc-900/90 backdrop-blur-2xl border border-white/10 rounded-[28px] p-1.5 shadow-2xl focus-within:border-cyan-500/30 transition-all flex items-end">
              <textarea
                ref={textareaRef}
                rows={1}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(input, false); } }}
                disabled={activeChat?.isEnded}
                placeholder={activeChat?.isEnded ? "Sesión terminada. Crea un nuevo chat..." : "Escribile a Hermes... (/exit para salir)"}
                className="flex-1 bg-transparent py-3 px-4 focus:outline-none text-[15px] resize-none min-h-[44px] max-h-[250px] custom-scrollbar text-white placeholder:text-zinc-600 disabled:opacity-50"
              />
              
              {input && (
                <button onClick={() => setInput('')} className="p-3 text-zinc-600 hover:text-red-400 transition-colors">
                  <X size={18} />
                </button>
              )}

              <button 
                onClick={() => handleSend(input, false)}
                disabled={!input.trim() || activeChat?.isEnded}
                className="p-3 bg-cyan-600 hover:bg-cyan-500 rounded-[22px] transition-all disabled:opacity-10 m-1 text-white"
              >
                <Send size={18} />
              </button>
            </div>
            
          </div>
        </div>
      </main>

      <style jsx global>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 4px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: transparent;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: rgba(255, 255, 255, 0.05);
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: rgba(255, 255, 255, 0.1);
        }
      `}</style>
    </div>
  );
}
